import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { createUseStyles, useTheme, Button, Icon, Markdown, Progress, Text } from 'hoi-poi-ui';
import { getLocaleFormat, truncate } from "../../../utils/numbers";
import { useWeb5Context } from "../../../lib/Web5Context";
import styles from "../styles/UsageCard";
const useStyles = createUseStyles(styles, 'UsageCard');
const LOCALE_FORMAT = {
  useGrouping: true,
  minimumFractionDigits: 0
};
const UsageCard = /*#__PURE__*/memo(_ref => {
  let {
    title,
    icon,
    disabled,
    onHover,
    progress,
    progressCustomFormat,
    locale,
    info,
    itemCode,
    linkCallback
  } = _ref;
  const classes = useStyles();
  const theme = useTheme();
  const {
    t
  } = useWeb5Context();
  const percentage = useMemo(() => {
    if (!progress || isNaN(progress.value) || isNaN(progress.max)) {
      return null;
    }
    const {
      value,
      max
    } = progress;
    const percentage = value === 0 && max === 0 ? 0 : truncate((value / max * 100).toFixed(1), 1) || 0;
    return `${getLocaleFormat(percentage, locale, LOCALE_FORMAT)}%`;
  }, [locale, progress]);
  const rootProps = useMemo(() => ({
    className: classNames(classes.root, {
      [classes.hasInfo]: !!info
    }),
    onMouseEnter: () => onHover && onHover(itemCode)
  }), [classes.hasInfo, classes.root, info, onHover, itemCode]);
  const iconProps = useMemo(() => ({
    name: icon,
    className: classes.icon,
    color: disabled ? theme.colors.utility.textDisabled : theme.colors.actionMajor[500]
  }), [classes.icon, disabled, icon, theme]);
  const progressProps = useMemo(() => _objectSpread(_objectSpread({}, progress), {}, {
    animation: {
      animationDuration: '1s',
      animationTimingFunction: 'cubic-bezier(.6, 0, .5, .9)'
    },
    className: classes.progress
  }), [classes.progress, progress]);
  const text = useMemo(() => {
    switch (true) {
      case !!progress:
        let value = getLocaleFormat(progress === null || progress === void 0 ? void 0 : progress.value, locale, LOCALE_FORMAT);
        let max = getLocaleFormat(progress === null || progress === void 0 ? void 0 : progress.max, locale, LOCALE_FORMAT);
        if (progressCustomFormat) {
          value = progressCustomFormat(value);
          max = progressCustomFormat(max);
        }
        return t('label_in_use_from_where', [value, max]);
      case disabled:
        return t('label_feature_no_contracted');
      default:
        break;
    }
  }, [disabled, locale, progress, progressCustomFormat, t]);
  const hoverContent = useMemo(() => {
    if (!info || typeof info !== 'object') return null;
    const {
      text,
      cta
    } = info;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.hoverContent
    }, text && /*#__PURE__*/React.createElement(Markdown, {
      content: text,
      overrides: {
        a: {
          target: '_blank',
          type: 'caption'
        },
        p: {
          color: theme.colors.textLight.secondary,
          type: 'caption'
        }
      },
      className: classes.hoverContentMarkdown,
      linkCallback: linkCallback
    }), cta.length && /*#__PURE__*/React.createElement("div", {
      className: classes.hoverContentCTA
    }, cta.map((button, index) => /*#__PURE__*/React.createElement(Button, {
      type: button.type,
      onClick: button.onClick,
      size: "small",
      key: index
    }, button.text))));
  }, [classes.hoverContent, classes.hoverContentCTA, classes.hoverContentMarkdown, info, linkCallback, theme]);
  return /*#__PURE__*/React.createElement("div", rootProps, /*#__PURE__*/React.createElement("div", {
    className: classes.header
  }, /*#__PURE__*/React.createElement(Icon, iconProps), /*#__PURE__*/React.createElement(Text, {
    type: "subtitle",
    medium: true,
    color: theme.colors.textLight.primary
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: classes.footer
  }, /*#__PURE__*/React.createElement(Text, {
    type: "caption",
    color: theme.colors.textLight.secondary
  }, text), percentage && /*#__PURE__*/React.createElement(Text, {
    type: "caption",
    color: theme.colors.textLight.primary,
    className: classes.percentage
  }, percentage), /*#__PURE__*/React.createElement(Progress, progressProps)), hoverContent);
});
export default UsageCard;